import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/Layout";
import { initials } from "@/components/site/data";
import { api } from "@/lib/api";
import { resolveSpeakerImageUrl } from "@/lib/events";
import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";

// Speaker detail page route

interface Speaker {
  id: number;
  name: string;
  role: string;
  about: string | null;
  description: string | null;
  image_url: string | null;
}

export const Route = createFileRoute("/speakers/$speakerId")({
  component: SpeakerDetail,
});

function SpeakerDetail() {
  const { speakerId } = Route.useParams() as { speakerId: string };
  const [speaker, setSpeaker] = useState<Speaker | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  console.log("SpeakerDetail component mounted, speakerId:", speakerId);

  useEffect(() => {
    loadSpeaker();
  }, [speakerId]);

  const loadSpeaker = async () => {
    console.log("Loading speaker with ID:", speakerId);
    try {
      const response = await api.get<Speaker>(`/api/speakers/${speakerId}`);
      console.log("API response:", response);
      if (response.success && response.data) {
        setSpeaker(response.data);
        console.log("Speaker data set:", response.data);
      } else {
        setError("Speaker not found");
        console.log("Speaker not found in response");
      }
    } catch (err) {
      console.error("Failed to load speaker:", err);
      setError("Failed to load speaker details");
    } finally {
      setLoading(false);
      console.log("Loading set to false");
    }
  };

  if (loading) {
    return (
      <>
        <PageHero
          eyebrow="Speaker Profile"
          title="Loading..."
          subtitle=""
        />
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center text-muted-foreground">Loading speaker details...</div>
        </section>
      </>
    );
  }

  if (error || !speaker) {
    return (
      <>
        <PageHero
          eyebrow="Speaker Profile"
          title="Speaker Not Found"
          subtitle=""
        />
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-muted-foreground mb-6">{error || "Speaker not found"}</p>
            <Link to="/speakers" className="inline-flex items-center text-primary hover:text-accent transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Speakers
            </Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Speaker Profile"
        title={speaker.name}
        subtitle={speaker.role}
      />
      <section className="py-12 sm:py-16 md:py-8 px-4 sm:px-6 lg:px-8">
        <div className="w-full">
          <Link 
            to="/speakers" 
            className="inline-flex items-center text-primary hover:text-accent transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Speakers
          </Link>
          
          {/* Photo and Name Section */}
          <div className="bg-white/50 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-border mb-8">
            <div className="grid md:grid-cols-2 gap-8 p-8 lg:p-12">
              {/* Photo */}
              <div className="flex justify-center items-start">
                <div className="aspect-square max-w-xs w-full rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-primary to-primary/70">
                  {resolveSpeakerImageUrl(speaker.image_url) ? (
                    <img
                      src={resolveSpeakerImageUrl(speaker.image_url)!}
                      alt={speaker.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="font-serif text-6xl md:text-7xl text-accent/95 drop-shadow-lg">
                        {initials(speaker.name)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Name and Role */}
              <div className="flex flex-col justify-center">
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-primary mb-4 leading-tight">
                  {speaker.name}
                </h2>
                <div className="inline-block px-4 py-2 bg-primary/10 rounded-full w-fit">
                  <p className="text-sm md:text-base font-semibold text-primary">
                    {speaker.role}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Description Section */}
          {(speaker.about || speaker.description) && (
            <div className="bg-white/50 backdrop-blur-sm rounded-2xl shadow-xl p-8 lg:p-12 border border-border">
              {speaker.about && (
                <div className="mb-8">
                  <h3 className="font-serif text-2xl text-primary mb-4 flex items-center">
                    <span className="w-12 h-0.5 bg-primary/50 mr-3"></span>
                    About
                  </h3>
                  <div 
                    className="text-muted-foreground leading-relaxed prose prose-base max-w-none text-sm md:text-base"
                    dangerouslySetInnerHTML={{ __html: speaker.about }}
                  />
                </div>
              )}

              {speaker.description && (
                <div>
                  <h3 className="font-serif text-2xl text-primary mb-4 flex items-center">
                    <span className="w-12 h-0.5 bg-accent/50 mr-3"></span>
                    Biography
                  </h3>
                  <div 
                    className="text-muted-foreground leading-relaxed prose prose-base max-w-none text-sm md:text-base"
                    dangerouslySetInnerHTML={{ __html: speaker.description }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
